import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { opsApi, ApiRequestError, type Dispute, type PayoutRun } from '@pinchcrew/core'
import { t } from '../i18n'

export const useMoneyStore = defineStore('ops-money', () => {
  const disputes = ref<Dispute[]>([])
  const runs = ref<PayoutRun[]>([])
  const loading = ref(false)
  const busy = ref(false)
  const error = ref<string | null>(null)

  const openDisputes = computed(() => disputes.value.filter(d => !d.status.startsWith('resolved')))

  function fail(e: unknown): false {
    error.value = e instanceof ApiRequestError ? e.message : t('common.somethingWrong')
    return false
  }

  async function load() {
    loading.value = true
    error.value = null
    try {
      const [d, r] = await Promise.all([opsApi.listDisputes(), opsApi.listPayoutRuns()])
      disputes.value = d
      runs.value = r
    } catch (e) {
      fail(e)
    } finally {
      loading.value = false
    }
  }

  async function resolveDispute(
    id: string,
    outcome: 'resolved_worker' | 'resolved_org' | 'resolved_split',
    paidMinutes: number,
    note: string,
  ) {
    busy.value = true
    error.value = null
    try {
      const updated = await opsApi.resolveDispute(id, outcome, paidMinutes, note)
      const i = disputes.value.findIndex(d => d.id === id)
      if (i >= 0) disputes.value[i] = updated
      return true
    } catch (e) {
      return fail(e)
    } finally {
      busy.value = false
    }
  }

  async function prepareRun(start: string, end: string) {
    busy.value = true
    error.value = null
    try {
      const run = await opsApi.preparePayoutRun(start, end)
      runs.value = [run, ...runs.value]
      return run
    } catch (e) {
      fail(e)
      return null
    } finally {
      busy.value = false
    }
  }

  async function releaseRun(id: string) {
    busy.value = true
    error.value = null
    try {
      const run = await opsApi.releasePayoutRun(id)
      const i = runs.value.findIndex(r => r.id === id)
      if (i >= 0) runs.value[i] = run
      return true
    } catch (e) {
      return fail(e)
    } finally {
      busy.value = false
    }
  }

  function clearError() { error.value = null }

  return { disputes, runs, loading, busy, error, openDisputes, load, resolveDispute, prepareRun, releaseRun, clearError }
})
